import React, { useEffect, useState } from 'react';
import { message, Drawer } from 'antd';
import LinkInvoiceModal from './LinkInvoiceModal';
import CreateInvoiceModal from './CreateInvoiceModal';
import { Button, Space, Card, Typography, Tooltip } from 'antd';
import CreateExpenseModal from './CreateExpenseModal';
import LinkExpenseModal from './LinkExpenseModal';
import { PlusOutlined, LinkOutlined, FileAddOutlined, FileSyncOutlined, EyeOutlined } from '@ant-design/icons';
import { useReconciliationStore } from '../store/reconciliationStore';
import { useAuth } from '../../../../AuthProvider';
import MatchSuggestionPanel from './MatchSuggestionPanel';
import { reconcileExpenseWithPaidY, reconcileInvoiceWithPaidY } from '../services/reconciliationApi';
import { saveBankTransactionsLocal } from '../basiq/service/BankTransactionsLocal';
import InvQuote from '../../../invoice/InvQuote';
import ExpenseItem from '../../../../components/pages/forms/ExpenseItem';

const { Text } = Typography;

const ReconciliationActions = (props) => {
  const { selectedTransaction, setSelectedTransaction, suggestions, setSuggestions, setTransactions, transactions } = useReconciliationStore();
  const auth = useAuth();

  const [isMatched, setIsMatched] = useState(selectedTransaction?.reconciliationStatus === 'MATCHED');
  const [isNotMatched, setIsNotMatched] = useState(selectedTransaction?.reconciliationStatus === 'UNMATCHED');

  // --- NEW STATES FOR RECONCILED VIEW ENGINE ---
  const [isReconciled, setIsReconciled] = useState(selectedTransaction?.reconciliationStatus === 'RECONCILED');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewType, setPreviewType] = useState(''); // 'INVOICE' or 'EXPENSE'
  const [previewId, setPreviewId] = useState('');

  useEffect(() => {
    console.log('Selected transaction changed:', selectedTransaction);
    setIsMatched(selectedTransaction?.reconciliationStatus === 'MATCHED');
    setIsNotMatched(selectedTransaction?.reconciliationStatus === 'UNMATCHED');
    setIsReconciled(selectedTransaction?.reconciliationStatus === 'RECONCILED');
  }, [selectedTransaction]);

  const reconcileTransaction = async (entity) => {
    const updatedTransaction = { ...selectedTransaction, associatedLinkedIds: [...(selectedTransaction.associatedLinkedIds || []), entity.id || entity.reimbursementId], reconciliationStatus: 'RECONCILED' };
    await saveBankTransactionsLocal(updatedTransaction, auth.token);
  }
  const reconcileTransactionAndUpdateExpense = async (expense) => {
    const updatedExpense = { ...expense, paid: 'Y', bankTransactionIds: [...(expense.bankTransactionIds || []), selectedTransaction.id] };
    await reconcileExpenseWithPaidY(updatedExpense, auth.token);
    reconcileTransaction(expense);
  }
  const reconcileTransactionAndUpdateInvoice = async (invoice) => {
    const updatedInvoice = { ...invoice, paid: 'Y', bankTransactionIds: [...(invoice.bankTransactionIds || []), selectedTransaction.id] };
    await reconcileInvoiceWithPaidY(updatedInvoice, auth.token);
    reconcileTransaction(invoice);
  }
  const handleAccept = async (suggestion) => {
    if (suggestion.entityType === 'EXPENSE') {
      reconcileTransactionAndUpdateExpense(suggestion.data);
    } else if (suggestion.entityType === 'INVOICE') {
      reconcileTransactionAndUpdateInvoice(suggestion.data);
    }

    setTransactions(transactions.map(tx => {
      if (tx.id === selectedTransaction?.id) {
        const entity = suggestion.data;
         const updatedTransaction = { ...selectedTransaction, associatedLinkedIds: [...(selectedTransaction.associatedLinkedIds || []), entity.id || entity.reimbursementId], reconciliationStatus: 'RECONCILED' };

        return { ...updatedTransaction };
      }
      return tx;
    }));
  };
  useEffect(() => {
    setSelectedTransaction(selectedTransaction.id);
  }, [transactions]);
  const handleReject = (suggestion) => {
    setSuggestions(suggestions.filter(s => s.id !== suggestion.id));
  };

  // State for Link Invoice modal
  const [showLinkInvoice, setShowLinkInvoice] = useState(false);
  const [showCreateInvoice, setShowCreateInvoice] = useState(false);

  const handleCreateInvoice = () => {
    setShowCreateInvoice(true);
  };
  // When a new invoice is created, mark as reconciled
  const handleInvoiceCreated = (invoice) => {
    setTransactions(transactions.map(tx =>
      tx.id === selectedTransaction?.id ? { ...tx, reconciliationStatus: 'RECONCILED', linkedInvoice: invoice.invoiceOrQuoteNumber } : tx
    ));
  };
  const handleLinkInvoice = () => {
    setShowLinkInvoice(true);
  };
  const [showCreateExpense, setShowCreateExpense] = useState(false);
  const [showLinkExpense, setShowLinkExpense] = useState(false);

  const handleCreateExpense = () => {
    setShowCreateExpense(true);
  };
  const handleLinkExpense = () => {
    setShowLinkExpense(true);
  };

  // When a new expense is created, mark as reconciled
  const handleExpenseCreated = (expense) => {
    setTransactions(transactions.map(tx =>
      tx.id === selectedTransaction?.id ? { ...tx, reconciliationStatus: 'RECONCILED', linkedExpense: true } : tx
    ));
    reconcileTransactionAndUpdateExpense(expense);
    message.success('Expense created and marked as paid!');
  };

  // When an expense is linked, mark as reconciled
  const handleExpenseLinked = (expense) => {
    setTransactions(transactions.map(tx =>
      tx.id === selectedTransaction?.id ? { ...tx, reconciliationStatus: 'RECONCILED', linkedExpense: expense.reimbursementId } : tx
    ));
    reconcileTransactionAndUpdateExpense(expense);
  };

  // When an array of invoices is selected from the modal (Updated to process split and multi-selections)
  const handleInvoiceSelected = async (invoicesArray) => {
    setShowLinkInvoice(false);

    // Safely structure as array regardless of single or multi select callback patterns
    const targetInvoices = Array.isArray(invoicesArray) ? invoicesArray : [invoicesArray];
    if (targetInvoices.length === 0) return;

    try {
      const combinedInvoicesTotal = targetInvoices.reduce((sum, inv) => sum + parseFloat(inv.grandTotal || inv.total || 0), 0);
      const bankAmount = parseFloat(selectedTransaction.amount);
      let remainingBankAllocation = bankAmount;

      for (let i = 0; i < targetInvoices.length; i++) {
        const invoice = targetInvoices[i];
        const invoiceTotal = parseFloat(invoice.grandTotal || invoice.total || 0);
        const isLastInvoice = i === targetInvoices.length - 1;

        if (combinedInvoicesTotal === bankAmount) {
          const invPaymentItems = [...(invoice.invPaymentItems ? invoice.invPaymentItems : []), {
                reference: 'reconciliation - spread over multiple invoices',
                date: new Date().toISOString().split('T')[0],
                amount: invoiceTotal,
                bankTransactionId: selectedTransaction.id,
              }];
          const updatedInvoice = { ...invoice, invPaymentItems: invPaymentItems,paid: 'Y', bankTransactionIds: [...(invoice.bankTransactionIds || []), selectedTransaction.id] };
          //const updatedInvoice = { ...invoice, paid: 'Y', bankTransactionIds: [...(invoice.bankTransactionIds || []), selectedTransaction.id] };
          await reconcileTransactionAndUpdateInvoice(updatedInvoice);
        } else {
          if (isLastInvoice) {
            console.log('Last invoice in selection. Allocating remaining bank amount:', invoice);
            console.log(invoice.grandTotal,selectedTransaction.amount);
            if (invoice.grandTotal !== selectedTransaction.amount) {
              console.log('Invoice total does not match transaction amount. Creating adjustment item for reconciliation.');
              const invPaymentItems = [...(invoice.invPaymentItems ? invoice.invPaymentItems : []), {
                reference: 'Adjustment/split for reconciliation',
                date: new Date().toISOString().split('T')[0],
                amount: selectedTransaction.amount,
                bankTransactionId: selectedTransaction.id,
              }];
              const updatedInvoice = { ...invoice, invPaymentItems: invPaymentItems };
              await reconcileTransaction(updatedInvoice);
              await reconcileInvoiceWithPaidY(updatedInvoice, auth.token);
              message.warning('Warning: The invoice total (' + invoice.grandTotal + ') does not match the transaction amount (' + selectedTransaction.amount + ').');
            } else {
              //const updatedInvoice = { ...invoice, paid: 'Y', bankTransactionIds: [...(invoice.bankTransactionIds || []), selectedTransaction.id] };
              reconcileTransactionAndUpdateInvoice(invoice);
              //await reconcileInvoiceWithPaidY(updatedInvoice, auth.token);
            }
          }
        }
      }

      const invoiceNumbersStr = targetInvoices.map(inv => inv.invoiceOrQuoteNumber).join(', ');

      setTransactions(transactions.map(tx =>
        tx.id === selectedTransaction?.id ? {
          ...tx,
          reconciliationStatus: 'RECONCILED',
          linkedInvoice: targetInvoices[0].invoiceOrQuoteNumber,
          associatedInvoiceNumbers: targetInvoices.map(inv => inv.invoiceOrQuoteNumber)
        } : tx
      ));

      if (combinedInvoicesTotal !== bankAmount) {
        message.warning('Warning: The invoice total (' + combinedInvoicesTotal + ') does not match the transaction amount (' + selectedTransaction.amount + ').');
      } else {
        message.success('Linked to invoices ' + invoiceNumbersStr + ' and marked as paid.');
      }
    } catch (e) {
      message.error('Failed to link invoice.');
    }
  };

  // --- NEW HANDLER TO TRIGGER DETAILED VIEWS ---
  const handleOpenItemView = (type, targetId) => {
    // Sets window search string location safely so InvQuoteView hook captures parameter logic correctly
    const newUrl = `${window.location.origin}${window.location.pathname}?id=${targetId}`;
    window.history.replaceState({ path: newUrl }, '', newUrl);
    setPreviewType(type);
    setPreviewId(targetId);
    setPreviewOpen(true);
  };

  if (!selectedTransaction) return null;

  // Only show suggestions if matched (reconciled), otherwise show unmatched actions
  return (
    <div>
      {isMatched && (
        <MatchSuggestionPanel
          suggestions={suggestions}
          onAccept={handleAccept}
          onReject={handleReject}
        />
      )}
      {isNotMatched && (
        <Card bordered style={{ marginTop: 24, background: '#fffbe6', borderColor: '#ffe58f' }}>
          <Text strong style={{ color: '#ad6800' }}>No matches found. What would you like to do?</Text>
          <div style={{ marginTop: 16 }}>
            <Space>
              {selectedTransaction.amount > 0 && <>
                <Tooltip title="Create a new invoice for this transaction">
                  <Button type="primary" icon={<FileAddOutlined />} onClick={handleCreateInvoice}>Create Invoice</Button>
                </Tooltip>
                <Tooltip title="Link to an existing invoice">
                  <Button icon={<LinkOutlined />} onClick={handleLinkInvoice}>Link Invoice</Button>
                </Tooltip>
              </>}
              {selectedTransaction.amount < 0 && <>
                <Tooltip title="Create a new expense for this transaction">
                  <Button type="primary" icon={<FileAddOutlined />} onClick={handleCreateExpense}>Create Expense</Button>
                </Tooltip>
                <Tooltip title="Link to an existing expense">
                  <Button icon={<LinkOutlined />} onClick={handleLinkExpense}>Link Expense</Button>
                </Tooltip>
              </>}
            </Space>
          </div>
        </Card>
      )}

      {/* --- NEW STATE SHIFT CONTAINER: DYNAMIC DISPLAY ON RECONCILED = TRUE --- */}
      {isReconciled && (
        <Card bordered style={{ marginTop: 24, background: '#f6ffed', borderColor: '#b7eb8f' }}>
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Text strong style={{ color: '#389e0d', fontSize: '15px' }}>
              ✓ Transaction Successfully Reconciled
            </Text>

            {/* Logic extraction reads linked entity data fields */}
            {selectedTransaction.direction === 'credit' ? (
              <div>
                <Text type="secondary">Associated Invoice Code: </Text>
                <Text strong>
                  {selectedTransaction.associatedInvoiceNumbers
                    ? selectedTransaction.associatedInvoiceNumbers.join(', ')
                    : (selectedTransaction.linkedInvoice || 'Linked Invoice Entity')}
                </Text>
                <div style={{ marginTop: 12 }}>
                  <Button
                    type="dashed"
                    icon={<EyeOutlined />}
                    onClick={() => handleOpenItemView('INVOICE', selectedTransaction.associatedLinkedIds?.[0] || selectedTransaction.id)}
                  >
                    View Linked Invoice Summary
                  </Button>
                </div>
              </div>
            ) : (
              <div>
                <Text type="secondary">Associated Expense ID: </Text>
                <Text code>{selectedTransaction.associatedLinkedIds?.[0] || 'Linked Expense Entity'}</Text>
                <div style={{ marginTop: 12 }}>
                  <Button
                    type="dashed"
                    icon={<EyeOutlined />}
                    onClick={() => handleOpenItemView('EXPENSE', selectedTransaction.associatedLinkedIds?.[0])}
                  >
                    View Linked Expense Summary
                  </Button>
                </div>
              </div>
            )}
          </Space>
        </Card>
      )}

      <LinkInvoiceModal open={showLinkInvoice} onClose={() => setShowLinkInvoice(false)} onSelect={handleInvoiceSelected} />
      <CreateInvoiceModal
        open={showCreateInvoice}
        onClose={() => setShowCreateInvoice(false)}
        onCreated={handleInvoiceCreated}
        transaction={selectedTransaction}
      />
      <CreateExpenseModal
        open={showCreateExpense}
        onClose={() => setShowCreateExpense(false)}
        onCreated={handleExpenseCreated}
      />
      <LinkExpenseModal
        open={showLinkExpense}
        onClose={() => setShowLinkExpense(false)}
        onSelect={handleExpenseLinked}
      />

      {/* --- NEW DYNAMIC PREVIEW SURFACE INJECTION --- */}
      <Drawer
        title={previewType === 'INVOICE' ? "Linked Invoice Overview Statement" : "Linked Expense Breakdown Statement"}
        placement="right"
        width={900}
        onClose={() => setPreviewOpen(false)}
        open={previewOpen}
        destroyOnClose
      >
        {previewType === 'INVOICE' ? (
          <InvQuote id={previewId} />
        ) : (
          <ExpenseItem reId={previewId} />
        )}
      </Drawer>
    </div>
  );
};

export default ReconciliationActions;